import { useState } from 'react';
import { Modal, Upload, message, Progress } from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import { uploadAudio, getFileName } from '../../store/actions/audio';
import { useSelector, useDispatch } from 'react-redux';
import { completeEncounter } from '../../store/actions/encounter';

const { Dragger } = Upload;

export const AudioUploadModal = ({
  visible,
  onClose,
  encounterId,
  encounterPhase,
  initTranscript,
  storedParams,
}) => {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadComplete, setUploadComplete] = useState(false);
  const { fileNameResponse } = useSelector((state) => state?.audioState);
  const dispatch = useDispatch();
  const [fileList, setFileList] = useState([]);
  const [xhrRequest, setXhrRequest] = useState(null);

  const uploadProps = {
    name: 'file',
    multiple: false,
    maxCount: 1,
    accept: '.mp3,.mp4,.wav,.m4a',
    showUploadList: true,
    beforeUpload: (file) => {
      const isAudioFile = /\.(mp3|mp4|wav|m4a)$/i.test(file.name);
      const isLessThan100MB = file.size / 1024 / 1024 < 100;

      if (!isAudioFile) {
        message.error('You can only upload MP3, MP4, WAV, or M4A files!');
        return Upload.LIST_IGNORE;
      }
      if (!isLessThan100MB) {
        message.error('File must be smaller than 100MB!');
        return Upload.LIST_IGNORE;
      }

      if (fileList.length >= 1) {
        setFileList([]);
      }
      return true;
    },
    customRequest: async ({ file, onSuccess, onError }) => {
      try {
        setUploading(true);
        setUploadProgress(0);

        // Get presigned URL
        const fetchAudio = uploadAudio(
          encounterId,
          encounterPhase,
          file.name,
          file
        );
        const response = await fetchAudio(dispatch);

        const { upload_url } = response;

        // Convert file to ArrayBuffer
        const fileData = await file.arrayBuffer();

        const uploadWithProgress = (url, data) => {
          return new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();
            setXhrRequest(xhr);
            xhr.upload.onprogress = (event) => {
              const progress = Math.round((event.loaded * 100) / event.total);
              setUploadProgress(progress);
            };
            xhr.onload = () => resolve(xhr);
            xhr.onerror = () => reject(xhr);
            xhr.open('PUT', url);
            xhr.send(data);
          });
        };

        // Add progress tracking
        const uploadResponse = await uploadWithProgress(upload_url, fileData);
        if (uploadResponse.status !== 200) {
          throw new Error('Upload failed');
        }

        message.success('File uploaded successfully');
        setUploadComplete(true);
        dispatch(getFileName(encounterId, encounterPhase));
        onSuccess();
      } catch (error) {
        message.error('Upload failed');
        onError();
        setUploadComplete(false);
      } finally {
        setUploading(false);
        setUploadProgress(0);
        setXhrRequest(null);
      }
    },
    fileList,
    onChange: ({ fileList: newFileList }) => {
      setFileList(newFileList);
      if (uploadProgress === 100) {
        setUploadComplete(true);
      } else {
        setUploadComplete(false);
      }
    },
  };

  const handleCompleteEncounter = async () => {
    initTranscript.current = false;
    setFileList([]);
    await dispatch(
      completeEncounter(encounterId, encounterPhase, storedParams, true)
    );
    onClose();
  };

  const handleCancelUpload = () => {
    if (xhrRequest) {
      xhrRequest.abort();
      setUploading(false);
      setUploadProgress(0);
      setXhrRequest(null);
      setFileList([]);
      message.info('Upload canceled');
    }
  };

  const handleClose = () => {
    // Reset all states
    setUploading(false);
    setUploadProgress(0);
    setUploadComplete(false);
    setFileList([]);
    if (xhrRequest) {
      xhrRequest.abort();
      setXhrRequest(null);
    }
    onClose();
  };

  return (
    <Modal
      title="Upload Audio File"
      open={visible}
      onCancel={handleClose}
      footer={
        uploadComplete || fileNameResponse?.files?.length > 0 ? (
          <div className="flex justify-end">
            <button
              onClick={handleCompleteEncounter}
              className="px-4 py-2 bg-[#00D090] hover:bg-[#059669] text-white rounded-xl"
            >
              Generate Summary
            </button>
          </div>
        ) : uploading ? (
          <div className="flex justify-end">
            <button
              onClick={handleCancelUpload}
              className="px-4 py-2 bg-red-500 hover:bg-red-700 text-white rounded-xl"
            >
              Cancel Upload
            </button>
          </div>
        ) : null
      }
      width={600}
    >
      <Dragger {...uploadProps}>
        <p className="ant-upload-drag-icon">
          <InboxOutlined />
        </p>
        <p className="ant-upload-text">
          Click or drag audio file to this area to upload
        </p>
        <p className="ant-upload-hint">
          Supports MP3, MP4, WAV, and M4A files up to 100MB
        </p>
        <p className="ant-upload-hint">Only one file can be uploaded.</p>
      </Dragger>
      {uploading && <Progress percent={uploadProgress} />}
      {fileNameResponse?.files?.length > 0 && (
        <div className="mt-4 text-gray-700">
          Current audio file: {fileNameResponse?.files?.[0]?.['display_name']}
        </div>
      )}
    </Modal>
  );
};
